// In-memory store for the Spam feature counters ('Mark Chat as Spam' story).
// Kept in module scope (not localStorage) so it survives navigation within a session
// but resets on full page reload. Maintained globally from App.js so it is correct even
// when the user is not on /chat when a tenant socket event arrives.
//
// Two distinct counters:
//  - total          → number of spam conversations (the "Spam (N)" button counter).
//                     Seeded from GET /api/chats/spam-chats/ and adjusted by
//                     'conversation.marked_spam' (+) / 'conversation.removed_from_spam' (-).
//  - messageCounts  → per-conversation new-message count (the badge on each spam row),
//                     incremented by 'message.new.spam' and cleared when the conversation is opened.

const spamConversationIds = new Set();
const messageCounts = new Map();

const emitTotal = () => {
  window.dispatchEvent(
    new CustomEvent('spam_counter_changed', { detail: { count: spamConversationIds.size } })
  );
};

const buildCountsObject = () => {
  const counts = {};
  messageCounts.forEach((value, id) => {
    counts[id] = value;
  });
  return counts;
};

const emitMessageCounts = () => {
  window.dispatchEvent(
    new CustomEvent('spam_message_counts_changed', { detail: { counts: buildCountsObject() } })
  );
};

// Total shown in the Spam button (number of spam conversations).
export const getSpamCount = () => spamConversationIds.size;

// Per-conversation new-message counts, as a plain object { [conversationId]: count }.
export const getSpamMessageCounts = () => buildCountsObject();

// Seed the total from the spam-chats API response (array of conversation objects or ids).
export const setSpamConversations = (conversations) => {
  spamConversationIds.clear();
  (Array.isArray(conversations) ? conversations : []).forEach((c) => {
    const id = Number(c && typeof c === 'object' ? c.id : c);
    if (!Number.isNaN(id)) spamConversationIds.add(id);
  });
  emitTotal();
};

// 'conversation.marked_spam' → add to the spam set (+1 total, deduped).
export const markSpamConversation = (conversationId) => {
  const id = Number(conversationId);
  if (Number.isNaN(id)) return;
  if (!spamConversationIds.has(id)) {
    spamConversationIds.add(id);
    emitTotal();
  }
};

// 'conversation.removed_from_spam' → remove from the spam set (-1 total) and clear its badge.
export const removeSpamConversation = (conversationId) => {
  const id = Number(conversationId);
  if (spamConversationIds.delete(id)) {
    emitTotal();
  }
  if (messageCounts.delete(id)) {
    emitMessageCounts();
  }
};

// 'message.new.spam' → increment that conversation's new-message badge.
export const incrementSpamMessage = (conversationId) => {
  const id = Number(conversationId);
  if (Number.isNaN(id)) return;
  messageCounts.set(id, (messageCounts.get(id) || 0) + 1);
  emitMessageCounts();
};

// Conversation opened (read) → clear its new-message badge.
export const clearSpamConversationMessages = (conversationId) => {
  const id = Number(conversationId);
  if (messageCounts.delete(id)) {
    emitMessageCounts();
  }
};
